import type {
  ShopDefinition,
  ShopKind
} from '../gameplay/station';
import { getShopDefinition } from '../gameplay/station';
import type { Inventory } from '../gameplay/inventory';
import {
  ITEMS,
  ITEM_ORDER,
  listSellableItems
} from '../gameplay/inventory';
import type { ItemId } from '../gameplay/inventory';
import type { PlayerWallet } from '../gameplay/wallet';
import type { PlayerStats } from '../gameplay/player';
import type { ShipUpgrades, UpgradeId } from '../gameplay/upgrades';
import { UPGRADES } from '../gameplay/upgrades';
import type { FactionSelection } from '../gameplay/factions';
import type { MissionEventBus } from '../gameplay/missions';

export interface ShopOverlayDependencies {
  readonly wallet: PlayerWallet;
  readonly shipInventory: Inventory;
  readonly fpsInventory: Inventory;
  readonly stats: PlayerStats;
  readonly shipUpgrades: ShipUpgrades;
  readonly faction: FactionSelection;
  readonly missionBus: MissionEventBus;
  readonly onClose: () => void;
}

const REPAIR_COST_PER_HP = 5;
const REFUEL_COST_PER_UNIT = 3;

/**
 * Single overlay element used for all four counters. Swaps content based on
 * shop kind. Mounted once into the UI tree and toggled per interaction.
 */
export class ShopOverlay {
  readonly element: HTMLElement;

  private readonly bodyEl: HTMLElement;
  private readonly titleEl: HTMLElement;
  private readonly taglineEl: HTMLElement;
  private readonly rootWrap: HTMLElement;
  private readonly vendorQuoteEl: HTMLElement;
  private readonly feedbackEl: HTMLElement;

  private feedbackTimeout: number | null = null;
  private activeKind: ShopKind | null = null;
  private visible = false;

  private readonly unsubscribes: Array<() => void> = [];

  constructor(private readonly deps: ShopOverlayDependencies) {
    this.element = document.createElement('div');
    this.element.className = 'shop-overlay';

    const backdrop = document.createElement('div');
    backdrop.className = 'shop-backdrop';

    this.rootWrap = document.createElement('div');
    this.rootWrap.className = 'shop-root';

    const header = document.createElement('header');
    header.className = 'shop-header';
    this.titleEl = document.createElement('h2');
    this.titleEl.className = 'shop-title';
    this.taglineEl = document.createElement('div');
    this.taglineEl.className = 'shop-tagline';
    const closeBtn = document.createElement('button');
    closeBtn.className = 'shop-close';
    closeBtn.type = 'button';
    closeBtn.textContent = 'Close (E)';
    closeBtn.addEventListener('click', () => this.deps.onClose());
    const titleBlock = document.createElement('div');
    titleBlock.className = 'shop-title-block';
    titleBlock.append(this.titleEl, this.taglineEl);
    header.append(titleBlock, closeBtn);

    this.vendorQuoteEl = document.createElement('div');
    this.vendorQuoteEl.className = 'shop-quote';

    this.bodyEl = document.createElement('section');
    this.bodyEl.className = 'shop-body';

    this.feedbackEl = document.createElement('div');
    this.feedbackEl.className = 'shop-feedback';

    const footer = document.createElement('footer');
    footer.className = 'shop-footer';
    footer.innerHTML =
      '<span><kbd>E</kbd> or <kbd>ESC</kbd> to close</span>';

    this.rootWrap.append(header, this.vendorQuoteEl, this.bodyEl, this.feedbackEl, footer);
    this.element.append(backdrop, this.rootWrap);
  }

  show(kind: ShopKind): void {
    this.activeKind = kind;
    const def = getShopDefinition(kind);
    this.applyShopAccent(def);
    this.titleEl.textContent = def.displayName;
    this.taglineEl.textContent = def.tagline;
    this.vendorQuoteEl.textContent = this.pickQuote(kind);
    this.renderBody(kind);
    this.element.classList.add('is-visible');
    this.visible = true;
  }

  hide(): void {
    this.element.classList.remove('is-visible');
    this.visible = false;
    for (const u of this.unsubscribes.splice(0)) u();
    if (this.feedbackTimeout) {
      window.clearTimeout(this.feedbackTimeout);
      this.feedbackTimeout = null;
    }
  }

  isOpen(): boolean {
    return this.visible;
  }

  getActiveKind(): ShopKind | null {
    return this.activeKind;
  }

  dispose(): void {
    this.hide();
    this.element.remove();
  }

  private applyShopAccent(def: ShopDefinition): void {
    const accent = toHexString(def.accent);
    const accentGlow = toHexString(def.accentGlow);
    this.rootWrap.style.setProperty('--shop-accent', accent);
    this.rootWrap.style.setProperty('--shop-accent-glow', accentGlow);
  }

  private pickQuote(kind: ShopKind): string {
    switch (kind) {
      case 'ship-upgrade':
        return '«The right ship is the one that takes you home. Let\'s make yours go far.»';
      case 'suit-upgrade':
        return '«Your skin in the vacuum. A good suit is what keeps you breathing.»';
      case 'weapon-tool':
        return '«Every tool has its purpose. Choose with wisdom... or ferocity.»';
      case 'trade-terminal':
        return '«Everything converts to credits, friend. Every nugget, every gram.»';
    }
  }

  private renderBody(kind: ShopKind): void {
    this.bodyEl.innerHTML = '';
    this.feedbackEl.textContent = '';
    switch (kind) {
      case 'ship-upgrade':
        this.renderShipServices();
        break;
      case 'suit-upgrade':
        this.renderPlaceholder('Suit upgrades', [
          { name: 'Thermal resistance', price: 900, desc: 'Protection against extreme environments' },
          { name: 'Regenerator', price: 1400, desc: 'Recovers HP out of combat' },
          { name: 'Expanded backpack', price: 1800, desc: '+4 FPS inventory slots' },
          { name: 'Jetpack', price: 2400, desc: 'Powered jump on planets' }
        ]);
        break;
      case 'weapon-tool':
        this.renderWeaponTool();
        break;
      case 'trade-terminal':
        this.renderTradeTerminal();
        break;
    }
  }

  private renderShipServices(): void {
    const wrap = document.createElement('div');
    wrap.className = 'shop-trade';

    // Wallet display.
    const walletRow = document.createElement('div');
    walletRow.className = 'shop-wallet';
    walletRow.innerHTML =
      '<span class="shop-wallet-label">Balance</span>' +
      '<span class="shop-wallet-value">0 ₡</span>';
    const walletValue = walletRow.querySelector('.shop-wallet-value') as HTMLElement;
    const walletUnsub = this.deps.wallet.subscribe((snap) => {
      walletValue.textContent = `${snap.balance.toLocaleString('en-US')} ₡`;
    });
    this.unsubscribes.push(walletUnsub);
    wrap.appendChild(walletRow);

    const sub = document.createElement('h3');
    sub.className = 'shop-subheading';
    sub.textContent = 'Maintenance & Refuel';
    wrap.appendChild(sub);

    const grid = document.createElement('div');
    grid.className = 'shop-trade-grid';
    wrap.appendChild(grid);

    const stats = this.deps.stats;

    const rebuild = (): void => {
      grid.innerHTML = '';

      // ── Hull repair card ──────────────────────────────────────────
      {
        const missing = Math.round(stats.hullMax - stats.hull);
        const cost = missing * REPAIR_COST_PER_HP;
        const isFull = missing <= 0;
        const canAfford = this.deps.wallet.balance >= cost;

        const card = document.createElement('div');
        card.className = 'shop-trade-card shop-service-card shop-service-hull';
        card.style.setProperty('--item-accent', '#ff7272');
        card.style.setProperty('--item-glow', 'rgba(255, 114, 114, 0.4)');

        const icon = document.createElement('div');
        icon.className = 'shop-trade-icon';
        icon.innerHTML = `
<svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <path d="M24 4 L40 12 L40 28 C40 38 32 42 24 44 C16 42 8 38 8 28 L8 12 Z"
        fill="#ff7272" stroke="#5a1818" stroke-width="1.5"/>
  <path d="M24 4 L40 12 L40 28 C40 38 32 42 24 44 C16 42 8 38 8 28 L8 12 Z"
        fill="rgba(255,255,255,0.18)"/>
  <path d="M18 22 L24 16 L30 22 L30 32 L18 32 Z" fill="#fff"/>
</svg>`;

        const info = document.createElement('div');
        info.className = 'shop-trade-info';
        info.innerHTML = `
          <div class="shop-trade-name">Repair hull</div>
          <div class="shop-trade-desc">Restores your ship hull HP to the maximum.</div>
          <div class="shop-trade-meta">
            <span class="shop-trade-stock">Status: ${Math.round(stats.hull)} / ${stats.hullMax} HP</span>
            <span class="shop-trade-price">${REPAIR_COST_PER_HP} ₡ per HP</span>
          </div>`;

        const actions = document.createElement('div');
        actions.className = 'shop-trade-actions';
        const fullBtn = document.createElement('button');
        fullBtn.type = 'button';
        fullBtn.className = 'shop-btn shop-btn-primary';
        fullBtn.textContent = isFull
          ? 'Hull intact'
          : `Repair (${cost} ₡)`;
        fullBtn.disabled = isFull || !canAfford;
        fullBtn.addEventListener('click', () => {
          if (!this.deps.wallet.debit(cost)) return;
          stats.repairHull(missing);
          this.flashFeedback(`Hull repaired · -${cost} ₡`);
          rebuild();
        });
        actions.appendChild(fullBtn);

        card.append(icon, info, actions);
        grid.appendChild(card);
      }

      // ── Fuel refill card ──────────────────────────────────────────
      {
        const missing = Math.round(stats.fuelMax - stats.fuel);
        const cost = missing * REFUEL_COST_PER_UNIT;
        const isFull = missing <= 0;
        const canAfford = this.deps.wallet.balance >= cost;

        const card = document.createElement('div');
        card.className = 'shop-trade-card shop-service-card shop-service-fuel';
        card.style.setProperty('--item-accent', '#9aff3c');
        card.style.setProperty('--item-glow', 'rgba(154, 255, 60, 0.4)');

        const icon = document.createElement('div');
        icon.className = 'shop-trade-icon';
        icon.innerHTML = `
<svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <rect x="14" y="10" width="20" height="32" rx="3" fill="#9aff3c" stroke="#1a3a05" stroke-width="1.5"/>
  <rect x="16" y="14" width="16" height="18" fill="rgba(0,0,0,0.25)"/>
  <rect x="18" y="18" width="12" height="4" fill="#dcff9a"/>
  <line x1="20" y1="10" x2="20" y2="6" stroke="#1a3a05" stroke-width="2"/>
  <line x1="28" y1="10" x2="28" y2="6" stroke="#1a3a05" stroke-width="2"/>
</svg>`;

        const info = document.createElement('div');
        info.className = 'shop-trade-info';
        info.innerHTML = `
          <div class="shop-trade-name">Refuel</div>
          <div class="shop-trade-desc">Fills the tank to maximum capacity.</div>
          <div class="shop-trade-meta">
            <span class="shop-trade-stock">Status: ${Math.round(stats.fuel)} / ${stats.fuelMax}</span>
            <span class="shop-trade-price">${REFUEL_COST_PER_UNIT} ₡ per unit</span>
          </div>`;

        const actions = document.createElement('div');
        actions.className = 'shop-trade-actions';
        const fullBtn = document.createElement('button');
        fullBtn.type = 'button';
        fullBtn.className = 'shop-btn shop-btn-primary';
        fullBtn.textContent = isFull
          ? 'Tank full'
          : `Refuel (${cost} ₡)`;
        fullBtn.disabled = isFull || !canAfford;
        fullBtn.addEventListener('click', () => {
          if (!this.deps.wallet.debit(cost)) return;
          const added = stats.addFuel(missing);
          if (added > 0) {
            this.deps.missionBus.emit('shipRefueled', {
              amount: added,
              credits: cost
            });
          }
          this.flashFeedback(`Tank full · -${cost} ₡`);
          rebuild();
        });
        actions.appendChild(fullBtn);

        card.append(icon, info, actions);
        grid.appendChild(card);
      }
    };

    rebuild();

    // Rebuild whenever stats or wallet change so prices/buttons stay live.
    this.unsubscribes.push(stats.subscribe(() => rebuild()));
    this.unsubscribes.push(this.deps.wallet.subscribe(() => rebuild()));

    // ── Modules section ───────────────────────────────────────────
    const modulesHeading = document.createElement('h3');
    modulesHeading.className = 'shop-subheading';
    modulesHeading.textContent = 'Modules';
    wrap.appendChild(modulesHeading);

    const modulesGrid = document.createElement('div');
    modulesGrid.className = 'shop-trade-grid';
    wrap.appendChild(modulesGrid);

    const rebuildModules = (): void => {
      modulesGrid.innerHTML = '';
      // Hauler-class hulls ship with the industrial beam already mounted —
      // surface that as a "factory-fitted" tag instead of a buy button.
      const beamPreinstalled = this.deps.faction.factionId === 'miners';
      this.renderUpgradeCard(modulesGrid, 'ship-mining-beam', {
        preinstalled: beamPreinstalled,
        preinstalledLabel: 'Factory-fitted (Miner hauler)'
      });
      this.renderUpgradeCard(modulesGrid, 'radioactive-recycler');
    };

    rebuildModules();
    this.unsubscribes.push(
      this.deps.shipUpgrades.subscribe(() => rebuildModules())
    );
    this.unsubscribes.push(
      this.deps.wallet.subscribe(() => rebuildModules())
    );

    this.bodyEl.appendChild(wrap);
  }

  /** Coming-soon placeholder used for stalls whose catalog isn't part of
   *  this milestone (currently the suit-upgrade counter). */
  private renderPlaceholder(
    heading: string,
    items: Array<{ name: string; price: number; desc: string }>
  ): void {
    const wrap = document.createElement('div');
    wrap.className = 'shop-placeholder';

    const sub = document.createElement('h3');
    sub.className = 'shop-subheading';
    sub.textContent = heading;
    wrap.appendChild(sub);

    const note = document.createElement('p');
    note.className = 'shop-note';
    note.textContent = 'Catalog coming soon — the vendor is finalizing the stock.';
    wrap.appendChild(note);

    const grid = document.createElement('div');
    grid.className = 'shop-item-grid';
    for (const item of items) {
      const card = document.createElement('div');
      card.className = 'shop-item-card is-disabled';
      const name = document.createElement('div');
      name.className = 'shop-item-name';
      name.textContent = item.name;
      const desc = document.createElement('div');
      desc.className = 'shop-item-desc';
      desc.textContent = item.desc;
      const price = document.createElement('div');
      price.className = 'shop-item-price';
      price.textContent = `${item.price.toLocaleString('en-US')} ₡`;
      const soon = document.createElement('div');
      soon.className = 'shop-item-soon';
      soon.textContent = 'Coming soon';
      card.append(name, desc, price, soon);
      grid.appendChild(card);
    }
    wrap.appendChild(grid);
    this.bodyEl.appendChild(wrap);
  }

  private renderWeaponTool(): void {
    const wrap = document.createElement('div');
    wrap.className = 'shop-trade';

    const walletRow = document.createElement('div');
    walletRow.className = 'shop-wallet';
    walletRow.innerHTML =
      '<span class="shop-wallet-label">Balance</span>' +
      '<span class="shop-wallet-value">0 ₡</span>';
    const walletValue = walletRow.querySelector('.shop-wallet-value') as HTMLElement;
    this.unsubscribes.push(
      this.deps.wallet.subscribe((snap) => {
        walletValue.textContent = `${snap.balance.toLocaleString('en-US')} ₡`;
      })
    );
    wrap.appendChild(walletRow);

    const sub = document.createElement('h3');
    sub.className = 'shop-subheading';
    sub.textContent = 'Surface mining gear';
    wrap.appendChild(sub);

    const grid = document.createElement('div');
    grid.className = 'shop-trade-grid';
    wrap.appendChild(grid);

    const rebuild = (): void => {
      grid.innerHTML = '';
      this.renderUpgradeCard(grid, 'fps-mining-tool');
    };

    rebuild();
    this.unsubscribes.push(this.deps.shipUpgrades.subscribe(() => rebuild()));
    this.unsubscribes.push(this.deps.wallet.subscribe(() => rebuild()));

    this.bodyEl.appendChild(wrap);
  }

  /** Render a single purchasable upgrade as a trade card. `preinstalled`
   *  bypasses the buy flow and shows a factory-fitted tag (used for the
   *  ship-mining-beam on the miner hauler hull). */
  private renderUpgradeCard(
    grid: HTMLElement,
    id: UpgradeId,
    opts: { preinstalled?: boolean; preinstalledLabel?: string } = {}
  ): void {
    const upgrade = UPGRADES[id];
    const ownedByPurchase = this.deps.shipUpgrades.has(id);
    const owned = ownedByPurchase || !!opts.preinstalled;
    const canAfford = this.deps.wallet.balance >= upgrade.price;

    const card = document.createElement('div');
    card.className = 'shop-trade-card';
    card.style.setProperty('--item-accent', upgrade.accent);
    card.style.setProperty('--item-glow', upgrade.accentGlow);

    const icon = document.createElement('div');
    icon.className = 'shop-trade-icon';
    icon.innerHTML = upgrade.icon;

    const stockText = opts.preinstalled
      ? (opts.preinstalledLabel ?? 'Factory-fitted')
      : ownedByPurchase
        ? 'Installed'
        : 'Not installed';

    const info = document.createElement('div');
    info.className = 'shop-trade-info';
    info.innerHTML = `
      <div class="shop-trade-name">${upgrade.name}</div>
      <div class="shop-trade-desc">${upgrade.description}</div>
      <div class="shop-trade-meta">
        <span class="shop-trade-price">${upgrade.price.toLocaleString('en-US')} ₡</span>
        <span class="shop-trade-stock">${stockText}</span>
      </div>`;

    const actions = document.createElement('div');
    actions.className = 'shop-trade-actions';
    const buyBtn = document.createElement('button');
    buyBtn.type = 'button';
    buyBtn.className = 'shop-btn shop-btn-primary';
    if (owned) {
      buyBtn.textContent = opts.preinstalled ? 'Included' : 'Installed';
      buyBtn.disabled = true;
    } else {
      buyBtn.textContent = `Install (${upgrade.price} ₡)`;
      buyBtn.disabled = !canAfford;
      buyBtn.addEventListener('click', () => {
        if (this.deps.shipUpgrades.has(id)) return;
        if (!this.deps.wallet.debit(upgrade.price)) return;
        this.deps.shipUpgrades.install(id);
        this.deps.missionBus.emit('shopItemPurchased', {
          upgradeId: id,
          price: upgrade.price
        });
        this.flashFeedback(`${upgrade.name} installed · -${upgrade.price} ₡`);
      });
    }
    actions.appendChild(buyBtn);

    card.append(icon, info, actions);
    grid.appendChild(card);
  }

  private renderTradeTerminal(): void {
    const wrap = document.createElement('div');
    wrap.className = 'shop-trade';

    const walletRow = document.createElement('div');
    walletRow.className = 'shop-wallet';
    walletRow.innerHTML =
      '<span class="shop-wallet-label">Balance</span>' +
      '<span class="shop-wallet-value">0 ₡</span>';
    const walletValue = walletRow.querySelector('.shop-wallet-value') as HTMLElement;
    const walletUnsub = this.deps.wallet.subscribe((snap) => {
      walletValue.textContent = `${snap.balance.toLocaleString('en-US')} ₡`;
    });
    this.unsubscribes.push(walletUnsub);
    wrap.appendChild(walletRow);

    const sub = document.createElement('h3');
    sub.className = 'shop-subheading';
    sub.textContent = 'Sell your inventory';
    wrap.appendChild(sub);

    const grid = document.createElement('div');
    grid.className = 'shop-trade-grid';
    wrap.appendChild(grid);

    const sellable = listSellableItems();

    const rebuild = (): void => {
      grid.innerHTML = '';
      for (const item of sellable) {
        const shipCount = this.countOf(this.deps.shipInventory, item.id);
        const fpsCount = this.countOf(this.deps.fpsInventory, item.id);
        const total = shipCount + fpsCount;
        const card = document.createElement('div');
        card.className = 'shop-trade-card';
        card.style.setProperty(
          '--item-accent',
          item.accent
        );
        card.style.setProperty('--item-glow', item.accentGlow);

        const icon = document.createElement('div');
        icon.className = 'shop-trade-icon';
        icon.innerHTML = item.icon;

        const info = document.createElement('div');
        info.className = 'shop-trade-info';
        info.innerHTML = `
          <div class="shop-trade-name">${item.name}</div>
          <div class="shop-trade-desc">${item.description}</div>
          <div class="shop-trade-meta">
            <span class="shop-trade-price">${item.sellValue} ₡ / unit</span>
            <span class="shop-trade-stock">Ship: ${shipCount} · Suit: ${fpsCount}</span>
          </div>`;

        const actions = document.createElement('div');
        actions.className = 'shop-trade-actions';
        const sell1 = document.createElement('button');
        sell1.type = 'button';
        sell1.className = 'shop-btn shop-btn-secondary';
        sell1.textContent = 'Sell 1';
        sell1.disabled = total === 0;
        sell1.addEventListener('click', () => this.performSell(item.id, 1, rebuild));
        const sellAll = document.createElement('button');
        sellAll.type = 'button';
        sellAll.className = 'shop-btn shop-btn-primary';
        sellAll.textContent = `Sell all (${total})`;
        sellAll.disabled = total === 0;
        sellAll.addEventListener('click', () =>
          this.performSell(item.id, total, rebuild)
        );
        actions.append(sell1, sellAll);

        card.append(icon, info, actions);
        grid.appendChild(card);
      }
    };

    rebuild();
    const shipUnsub = this.deps.shipInventory.subscribe(() => rebuild());
    const fpsUnsub = this.deps.fpsInventory.subscribe(() => rebuild());
    this.unsubscribes.push(shipUnsub, fpsUnsub);

    this.bodyEl.appendChild(wrap);
  }

  private countOf(inv: Inventory, id: ItemId): number {
    let total = 0;
    const snap = inv.snapshot();
    for (const slot of snap) {
      if (slot && slot.itemId === id) total += slot.count;
    }
    return total;
  }

  private performSell(
    itemId: ItemId,
    requested: number,
    rebuild: () => void
  ): void {
    if (requested <= 0) return;
    const def = ITEMS[itemId];
    if (!def || !def.sellable) return;

    // Sell from ship first, then fps.
    let toSell = requested;
    toSell -= this.drainInventory(this.deps.shipInventory, itemId, toSell);
    if (toSell > 0) {
      toSell -= this.drainInventory(this.deps.fpsInventory, itemId, toSell);
    }
    const sold = requested - toSell;
    if (sold <= 0) return;
    const credits = sold * def.sellValue;
    this.deps.wallet.credit(credits);
    this.deps.missionBus.emit('tradeSaleCompleted', {
      itemId,
      quantity: sold,
      credits
    });
    this.flashFeedback(
      `+${credits} ₡ · ${sold}× ${def.shortName ?? def.name}`
    );
    rebuild();
  }

  private drainInventory(inv: Inventory, itemId: ItemId, requested: number): number {
    let drained = 0;
    const snap = inv.snapshot();
    for (let i = 0; i < snap.length && drained < requested; i++) {
      const slot = snap[i];
      if (!slot || slot.itemId !== itemId) continue;
      const take = Math.min(slot.count, requested - drained);
      const taken = inv.takeFromSlot(i, take);
      if (taken) drained += taken.count;
    }
    return drained;
  }

  private flashFeedback(message: string): void {
    this.feedbackEl.textContent = message;
    this.feedbackEl.classList.remove('is-visible');
    void this.feedbackEl.offsetWidth;
    this.feedbackEl.classList.add('is-visible');
    if (this.feedbackTimeout) window.clearTimeout(this.feedbackTimeout);
    this.feedbackTimeout = window.setTimeout(() => {
      this.feedbackEl.classList.remove('is-visible');
    }, 2000);
  }
}

function toHexString(color: number): string {
  return `#${color.toString(16).padStart(6, '0')}`;
}

// Keep references used for item ordering.
void ITEM_ORDER;
